/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file CorrelationRiskRateProgressionView.jsx
 * @author Daniel Aguirre
 * @description Recommended By Scheduling View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DashboardCard from 'components/DashboardCard';
import DashboardBar from 'components/Charts/DashboardBar';
import styleGuideColors from 'commons/styleGuideColors';
import { useHistory } from 'react-router';
import useTruncateAndFormat from 'hook/useTruncateAndFormat';

/**
 * CorrelationRiskRateProgression View
 * @param { Object } dashboardStaffActions - DashboardStaffActions Actions.
 * @return { Node } React node.
 */

function CorrelationRiskRateProgressionView({
  dashboardStaffActions,
  clinicId
}) {
  const history = useHistory();
  const [chartData, setChartData] = useState(null);
  const { t: translate } = useTranslation();

  const { getCorrelationRiskRateProgression } = dashboardStaffActions;

  const onGetData = (_, resp) => {
    const labels = [
      translate('dashboardStaff.lowProgresor'),
      translate('dashboardStaff.mediumProgresor'),
      translate('dashboardStaff.highProgresor')
    ];
    const proggressions = [
      'Bajo Progresor',
      'Medio Progresor',
      'Alto Progresor'
    ];
    const risks = ['Bajo', 'Medio', 'Alto'];
    const datasets = [
      {
        label: '<3',
        classification: 'Bajo',
        proggressionVelocity: 'Bajo Progresor',
        data: [0, 0, 0],
        values: [0, 0, 0],
        backgroundColor: styleGuideColors.baxterBrandTeal
      },
      {
        label: '3-5',
        classification: 'Medio',
        proggressionVelocity: 'Medio Progresor',
        data: [0, 0, 0],
        values: [0, 0, 0],
        backgroundColor: styleGuideColors.graphYellow
      },
      {
        label: '≥5',
        classification: 'Alto',
        proggressionVelocity: 'Alto Progresor',
        data: [0, 0, 0],
        values: [0, 0, 0],
        backgroundColor: styleGuideColors.alertError
      }
    ];

    let total = 0;

    if (resp?.data) {
      resp.data.forEach(({ cantidad, clasificacion }) => {
        if (clasificacion) {
          total += cantidad;
        }
      });
      resp.data.forEach((item) => {
        const findData = datasets.find(
          ({ proggressionVelocity }) =>
            proggressionVelocity === item.velocidad_progresion
        );
        const percentage = (item.cantidad * 100) / total;
        const riskIndex = risks.findIndex(
          (label) => label === item.clasificacion
        );
        if (findData) {
          findData.data[riskIndex] = useTruncateAndFormat(percentage, 2);
          findData.values[riskIndex] = item.cantidad;
        }
      });
    }
    setChartData({
      labels,
      datasets,
      proggressions,
      risks
    });
  };
  useEffect(() => {
    getCorrelationRiskRateProgression({ clinicId }, onGetData);
  }, [clinicId, translate]);
  const handleDetailClick = (index, datasetIndex) => {
    const totalItems = chartData.datasets[datasetIndex].values[index];
    const classification = chartData.risks[index];
    const proggression = chartData.proggressions[datasetIndex];
    history.push(
      `/main/dashboard-detail/correlation-risk-rate-progression/${classification}/${proggression}/${totalItems}`
    );
  };

  return (
    <DashboardCard id="F18" loading={!chartData}>
      <DashboardBar
        title={translate('dashboardStaff.correlationRiskRateProgression')}
        yTitle={translate('dashboardStaff.percentageOfPatients')}
        labelDescription={`${translate('dashboardStaff.progressionVelocity')}:`}
        dataValues={chartData}
        stacked
        percentScale
        onDetailClick={handleDetailClick}
      />
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
CorrelationRiskRateProgressionView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getCorrelationRiskRateProgression: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default CorrelationRiskRateProgressionView;
